import type { CaseStudy } from '../types';

export const ftx: CaseStudy = {
  id: 'ftx',
  order: 6,
  title: 'FTX',
  english: 'FTX',
  category: 'finance',
  era: '2019 — 2022',
  region: '巴哈马 / 全球',
  accent: '#a7782f',
  hook: '它不是死于“加密冬天”，而是死于一个更古老的问题：客户的钱被当成了自己的钱，而外部看到的只有增长和声誉。',
  question: '为什么一家被顶级机构、名人和媒体视为“可信”的交易所，能在几周内崩塌？',
  summary:
    'FTX 的失败同时包含托管滥用、关联交易、循环抵押与治理失效。它提醒我们：信任不能只靠创始人形象和品牌背书，必须落到资产隔离、独立审计与可执行的追责机制上。',
  thesis: '金融中介的核心产品不是收益，而是「他人资金的安全」；当安全依赖信任叙事而非制度，崩塌只是时间问题。',
  quote: '最危险的金融创新，不是收益率太高，而是让你无法回答：我的钱现在在哪里？',
  entryNodeIds: ['ftx-custody', 'ftx-related', 'ftx-token'],
  focusNodeId: 'ftx-custody',
  lensIds: ['asymmetry', 'incentives', 'mismatch', 'reflexivity', 'regulation'],
  opportunityIds: ['opp-custody-audit'],
  sourceIds: ['doj-ftx-charges', 'doj-ftx-sentencing'],
  facts: [
    {
      text: 'FTX 于 2019 年成立，2022 年 11 月申请破产；美国司法部随后对创始人 Sam Bankman-Fried 提出欺诈等指控。',
      sourceIds: ['doj-ftx-charges'],
    },
    {
      text: '2023 年，陪审团裁定 Bankman-Fried 多项欺诈与共谋罪名成立；2024 年 3 月，他被判处 25 年监禁。',
      sourceIds: ['doj-ftx-sentencing'],
    },
    {
      text: '检方在起诉与庭审中称，客户资金被转移至关联交易公司 Alameda Research，并用于投资、政治捐款和偿还债务等；陪审团已裁定多项欺诈与共谋罪名成立。',
      sourceIds: ['doj-ftx-charges', 'doj-ftx-sentencing'],
    },
  ],
  nodes: [
    {
      id: 'ftx',
      caseId: 'ftx',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: 'FTX',
      summary: '高速增长的加密交易所，在挤兑中暴露客户资金缺口。',
      detail:
        'FTX 以交易体验、营销和“有效利他主义”形象吸引了大量用户与机构。它对外呈现为一家高速增长、受机构背书的金融中介，但关键问题在于：客户资产是否真正隔离？关联交易是否被独立审查？当用户开始提款，答案才被迫公开。',
      boundary: '本案例讨论的是 FTX 的具体治理与资金挪用问题，不应推广为“所有加密交易所都不安全”的结论。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['doj-ftx-charges'],
    },
    {
      id: 'ftx-custody',
      caseId: 'ftx',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '托管滥用：客户资产不是公司资产',
      summary: '交易所代客户保管资产，一旦缺少隔离与审计，就可能被挪去自营、投资或补窟窿。',
      detail:
        '用户把币存进交易所，看到的是账户余额，实际控制权在平台。正常的中介会把客户资产与自有资产隔离，并接受审计和监管。若隔离失效，平台就可以用客户的钱承担自己的风险；用户直到提款失败才发现。',
      boundary: '托管不一定需要传统银行牌照，但必须有可验证的资产隔离、储备证明与责任追究。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['doj-ftx-charges'],
    },
    {
      id: 'ftx-related',
      caseId: 'ftx',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '关联交易：自己人拥有特权',
      summary: '交易公司 Alameda 与交易所关系密切，获得特殊安排，风险在同一集团内部循环。',
      detail:
        '当交易所与做市商由同一批人控制，做市商可以享有更高杠杆、更快提款或特殊额度。对外部用户，这既是竞争不公平，也是风险集中：关联方的亏损最终可能由平台客户承担。',
      boundary: '关联交易并非天然违法；关键在于披露、独立审批、公平条款与风险隔离。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['doj-ftx-charges'],
    },
    {
      id: 'ftx-token',
      caseId: 'ftx',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '循环抵押：用自己发的资产证明自己有钱',
      summary: '关联代币被用作抵押或估值支撑，价格下跌会同时打击资产与信心。',
      detail:
        '如果一家公司持有的“资产”主要由自己或关联方发行，估值就带有反身性：市场相信它值钱时，资产负债表看起来健康；一旦价格下跌，抵押品缩水，追加保证金和提款压力同时出现。账面财富与可兑现现金是两件事。',
      boundary: '并非所有代币都缺乏价值；问题在于估值是否独立、流动性是否真实、抵押品是否集中在关联方。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['doj-ftx-charges'],
    },
    {
      id: 'ftx-governance',
      caseId: 'ftx',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '治理失效：声誉替代了控制',
      summary: '明星创始人、顶级投资人和名人背书，让外部把“看起来可信”当成“已经可控”。',
      detail:
        'FTX 的品牌与创始人形象提供了强大的信任信号，但信号不等于治理。董事会、独立审计、风险委员会、关联交易审查、客户资产隔离，这些“无聊”的机制才是防止挪用的基础设施。当它们缺失，外部越信任，内部越缺少刹车。',
      boundary: '强治理也不能消灭欺诈，但会提高发现概率、限制损失并保留追责证据。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['doj-ftx-sentencing'],
    },
    {
      id: 'ftx-condition',
      caseId: 'ftx',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：离岸监管与牛市叙事',
      summary: '跨境监管碎片化、加密牛市和名人营销，降低了外部审查的摩擦。',
      detail:
        'FTX 在不同司法辖区运营，监管边界复杂；牛市让收益看起来合理，名人背书降低了普通用户的警惕。这些条件不构成欺诈的理由，但解释了为什么风险能积累得如此隐蔽。',
      boundary: '监管缺失不是欺诈的充分条件；同样的环境里也有合规经营的平台。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['doj-ftx-charges'],
    },
    {
      id: 'ftx-outcome',
      caseId: 'ftx',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：挤兑、破产与刑事定罪',
      summary: '提款潮暴露资金缺口，交易所破产，创始人被定罪判刑。',
      detail:
        '当用户集中提款，平台无法用流动资产满足赎回，缺口暴露，客户、债权人和监管机构同时介入。FTX 的崩塌不是一次市场波动导致的，而是治理问题在流动性冲击下的显性化。',
      boundary: '破产后的资产追回与客户赔付过程复杂，相关金额以法院与受托人文件为准。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['doj-ftx-sentencing'],
    },
    {
      id: 'ftx-risk',
      caseId: 'ftx',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：把信任外包给形象',
      summary: '“他看起来很聪明”“很多名人投了”是最容易被利用的信任捷径。',
      detail:
        '声誉、学历、慈善形象和名人关系，都是低成本信号；它们能在短时间内替代尽调。对普通用户，最实用的防线不是判断创始人是否聪明，而是确认：资产是否隔离、谁能动用、审计是谁、提款规则是什么、最坏情况谁承担。',
      boundary: '信任本身是必要的；问题在于把不可验证的形象当作可验证的制度。',
      evidence: 'opinion',
      confidence: 'high',
      sourceIds: ['doj-ftx-sentencing'],
    },
    {
      id: 'ftx-opp',
      caseId: 'ftx',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：把“可信”做成基础设施',
      summary: '托管、审计、合规、取证与保险，是信任成本上升后持续增长的服务需求。',
      detail:
        '每当信任危机发生，市场会愿意为可验证的安全付费：独立托管、储备与负债证明、链上取证、反洗钱、关联交易审查、保险与合规咨询。机会不在于承诺“绝对安全”，而在于把安全拆成可检查的流程和责任。',
      boundary: '“储备证明”只能证明某一时点的资产，不能证明负债与内部控制；合规服务也受牌照和专业能力限制。',
      evidence: 'hypothesis',
      confidence: 'medium',
      sourceIds: ['doj-ftx-sentencing'],
    },
  ],
  edges: [
    {
      id: 'ftx-e1',
      caseId: 'ftx',
      from: 'ftx',
      to: 'ftx-custody',
      label: '代客保管',
      explanation: '用户把资产交给平台，平台获得实际控制权，隔离机制决定风险边界。',
      tone: 'cause',
    },
    {
      id: 'ftx-e2',
      caseId: 'ftx',
      from: 'ftx',
      to: 'ftx-related',
      label: '集团内循环',
      explanation: '交易所与关联做市商由同一批人控制，资金与风险容易在内部转移。',
      tone: 'cause',
    },
    {
      id: 'ftx-e3',
      caseId: 'ftx',
      from: 'ftx-custody',
      to: 'ftx-token',
      label: '缺口需要“资产”填补',
      explanation: '当客户资金被挪用，关联代币和内部估值可以用来暂时掩盖缺口。',
      tone: 'cause',
    },
    {
      id: 'ftx-e4',
      caseId: 'ftx',
      from: 'ftx-related',
      to: 'ftx-token',
      label: '抵押品内部化',
      explanation: '关联方资产被用作抵押或估值支撑，风险与价格在同一集团内循环。',
      tone: 'cause',
    },
    {
      id: 'ftx-e5',
      caseId: 'ftx',
      from: 'ftx-governance',
      to: 'ftx-outcome',
      label: '缺少刹车',
      explanation: '没有独立监督与资产隔离，风险在被发现前持续累积。',
      tone: 'risk',
    },
    {
      id: 'ftx-e6',
      caseId: 'ftx',
      from: 'ftx-token',
      to: 'ftx-outcome',
      label: '抵押品缩水',
      explanation: '代币价格下跌会同时削弱资产负债表与市场信心，触发提款和追加保证金。',
      tone: 'cause',
    },
    {
      id: 'ftx-e7',
      caseId: 'ftx',
      from: 'ftx-condition',
      to: 'ftx-outcome',
      label: '外部摩擦降低',
      explanation: '离岸监管、牛市与名人营销，让外部审查和用户警惕同时下降。',
      tone: 'enable',
    },
    {
      id: 'ftx-e8',
      caseId: 'ftx',
      from: 'ftx-outcome',
      to: 'ftx-risk',
      label: '信任被重新定价',
      explanation: '崩塌后，市场才意识到声誉信号不能替代资产隔离与治理。',
      tone: 'risk',
    },
    {
      id: 'ftx-e9',
      caseId: 'ftx',
      from: 'ftx-risk',
      to: 'ftx-opp',
      label: '安全需求上升',
      explanation: '信任成本上升，会创造托管、审计、合规与取证服务的需求。',
      tone: 'enable',
    },
  ],
};
